import { ToastProvider, ThemeProvider, useRevealRoot } from '@memorizecards/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Suspense, lazy } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AdminShell } from './components/AdminShell';
import { AppShell } from './components/AppShell';
import { LimiteDeErro } from './components/LimiteDeErro';
import { CarregandoPagina } from './components/estado';
import { ApiError } from './lib/api';
import { AuthProvider, useAuth } from './state/auth';

import { Baralhos } from './routes/Baralhos';
import { Caderno } from './routes/Caderno';
import { Conta } from './routes/Conta';
import { Entrar } from './routes/Entrar';
import { Estudar } from './routes/Estudar';
import { Expirado } from './routes/Expirado';
import { Favoritas } from './routes/Favoritas';
import { Ajuda } from './routes/Ajuda';
import { NaoEncontrado } from './routes/NaoEncontrado';
import { EsqueciSenha, RedefinirSenha, TrocarSenha } from './routes/Senha';
import { Simulado } from './routes/Simulado';

// Os dois painéis de gráficos carregam o Recharts; separá-los tira ~180 KB do
// primeiro carregamento de quem entra só para estudar.
const Desempenho = lazy(() =>
  import('./routes/Desempenho').then((m) => ({ default: m.Desempenho })),
);
const Estatisticas = lazy(() =>
  import('./routes/Estatisticas').then((m) => ({ default: m.Estatisticas })),
);
const Cronograma = lazy(() =>
  import('./routes/Cronograma').then((m) => ({ default: m.Cronograma })),
);

const Resumos = lazy(() => import('./routes/Resumos').then((m) => ({ default: m.Resumos })));
const ResumoLeitor = lazy(() =>
  import('./routes/ResumoLeitor').then((m) => ({ default: m.ResumoLeitor })),
);

const AdminDashboard = lazy(() =>
  import('./routes/admin/Dashboard').then((m) => ({ default: m.AdminDashboard })),
);
const AdminUsuarios = lazy(() =>
  import('./routes/admin/Usuarios').then((m) => ({ default: m.AdminUsuarios })),
);
const AdminUsuarioDetalhe = lazy(() =>
  import('./routes/admin/UsuarioDetalhe').then((m) => ({ default: m.AdminUsuarioDetalhe })),
);
const AdminPagamentos = lazy(() =>
  import('./routes/admin/Pagamentos').then((m) => ({ default: m.AdminPagamentos })),
);
const AdminCupons = lazy(() =>
  import('./routes/admin/Cupons').then((m) => ({ default: m.AdminCupons })),
);
const AdminCadastros = lazy(() =>
  import('./routes/admin/Cadastros').then((m) => ({ default: m.AdminCadastros })),
);
const AdminFeedbacks = lazy(() =>
  import('./routes/admin/Feedbacks').then((m) => ({ default: m.AdminFeedbacks })),
);
const Feedback = lazy(() => import('./routes/Feedback').then((m) => ({ default: m.Feedback })));
const AdminPedidos = lazy(() =>
  import('./routes/admin/Pedidos').then((m) => ({ default: m.AdminPedidos })),
);
const AdminAuditoria = lazy(() =>
  import('./routes/admin/Auditoria').then((m) => ({ default: m.AdminAuditoria })),
);
const AdminEmails = lazy(() =>
  import('./routes/admin/Emails').then((m) => ({ default: m.AdminEmails })),
);
const AdminEtl = lazy(() =>
  import('./routes/admin/Etl').then((m) => ({ default: m.AdminEtl })),
);
const AdminBackup = lazy(() =>
  import('./routes/admin/Backup').then((m) => ({ default: m.AdminBackup })),
);
const AdminSegurancaHost = lazy(() =>
  import('./routes/admin/SegurancaHost').then((m) => ({ default: m.AdminSegurancaHost })),
);
const AdminUsoGratis = lazy(() =>
  import('./routes/admin/UsoGratis').then((m) => ({ default: m.AdminUsoGratis })),
);
const AdminUsoPlataforma = lazy(() =>
  import('./routes/admin/UsoPlataforma').then((m) => ({ default: m.AdminUsoPlataforma })),
);
const AdminUsoDetalhe = lazy(() =>
  import('./routes/admin/UsoDetalhe').then((m) => ({ default: m.AdminUsoDetalhe })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // O foco volta ao separador dezenas de vezes numa sessão de estudo;
      // recarregar tudo de cada vez seria trabalho inútil no servidor.
      refetchOnWindowFocus: false,
      staleTime: 15_000,
      retry: (falhas, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return falhas < 2;
      },
    },
  },
});

export function App(): ReactNode {
  return (
    <LimiteDeErro>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ToastProvider>
            <BrowserRouter>
              <AuthProvider>
                <Rotas />
              </AuthProvider>
            </BrowserRouter>
          </ToastProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </LimiteDeErro>
  );
}

function Rotas(): ReactNode {
  useRevealRoot();

  return (
    <Suspense fallback={<CarregandoPagina />}>
      <Routes>
        <Route path="/entrar" element={<Entrar />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />

        <Route element={<ExigeSessao />}>
          <Route path="/trocar-senha" element={<TrocarSenha />} />

          <Route element={<ExigeSuperadmin />}>
            <Route element={<AdminShell />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/usuarios" element={<AdminUsuarios />} />
              <Route path="/admin/usuarios/:id" element={<AdminUsuarioDetalhe />} />
              <Route path="/admin/pagamentos" element={<AdminPagamentos />} />
              <Route path="/admin/cupons" element={<AdminCupons />} />
              <Route path="/admin/cadastros" element={<AdminCadastros />} />
              <Route path="/admin/feedbacks" element={<AdminFeedbacks />} />
              <Route path="/admin/pedidos" element={<AdminPedidos />} />
              <Route path="/admin/etl" element={<AdminEtl />} />
              <Route path="/admin/uso" element={<AdminUsoPlataforma />} />
              <Route path="/admin/gratis" element={<AdminUsoGratis />} />
              <Route path="/admin/uso/:id" element={<AdminUsoDetalhe />} />
              <Route path="/admin/backup" element={<AdminBackup />} />
              <Route path="/admin/seguranca" element={<AdminSegurancaHost />} />
              <Route path="/admin/emails" element={<AdminEmails />} />
              <Route path="/admin/auditoria" element={<AdminAuditoria />} />
            </Route>
          </Route>

          <Route element={<AppShell />}>
            <Route path="/" element={<Navigate to="/baralhos" replace />} />
            <Route path="/conta" element={<Conta />} />
            <Route path="/ajuda" element={<Ajuda />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/assinatura" element={<Expirado />} />

            <Route element={<ExigeAssinatura />}>
              <Route path="/baralhos" element={<Baralhos />} />
              <Route path="/estudar" element={<Estudar />} />
              <Route path="/simulado" element={<Simulado />} />
              <Route path="/caderno" element={<Caderno />} />
              <Route path="/favoritas" element={<Favoritas />} />
              <Route path="/desempenho" element={<Desempenho />} />
              <Route path="/estatisticas" element={<Estatisticas />} />
              <Route path="/cronograma" element={<Cronograma />} />
              <Route path="/resumos" element={<Resumos />} />
              <Route path="/resumos/:id" element={<ResumoLeitor />} />
            </Route>

            <Route path="*" element={<NaoEncontrado />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

/**
 * Porta de entrada.
 *
 * Enquanto `/api/auth/me` não responde não se decide nada — redirecionar antes
 * disso mandaria para o login quem já tinha sessão válida, a cada recarga da
 * página.
 */
function ExigeSessao(): ReactNode {
  const { user, carregando } = useAuth();
  const location = useLocation();

  if (carregando) return <CarregandoPagina rotulo="Verificando sua sessão…" />;

  if (!user) {
    return (
      <Navigate to="/entrar" replace state={{ de: location.pathname + location.search }} />
    );
  }

  // Senha temporária: nada mais é acessível antes da troca.
  if (user.mustChangePassword && location.pathname !== '/trocar-senha') {
    return <Navigate to="/trocar-senha" replace />;
  }

  return <Outlet />;
}

function ExigeAssinatura(): ReactNode {
  const { user } = useAuth();
  if (user && !user.acesso.liberado) return <Navigate to="/assinatura" replace />;
  return <Outlet />;
}

function ExigeSuperadmin(): ReactNode {
  const { user } = useAuth();
  if (user?.role !== 'superadmin') return <Navigate to="/baralhos" replace />;
  return <Outlet />;
}
